import { assertSupabaseConfigured, supabase } from "../lib/supabase";
import type {
  FeatureFlag,
  FeatureFlagPackageDefault,
  FeatureFlagUserOverride,
  TenantPlan,
} from "../types/admin";

export const FEATURE_FLAG_KEYS = [
  "trainer_profiles_enabled",
  "client_profiles_enabled",
  "gym_profiles_enabled",
  "payments_enabled",
  "ai_enabled",
  "nutrition_enabled",
  "messaging_enabled",
  "check_ins_enabled",
  "progress_photos_enabled",
  "white_label_enabled",
  "demo_mode_enabled",
] as const;

export const featureFlagService = {
  async listFlags(): Promise<FeatureFlag[]> {
    assertSupabaseConfigured();

    const { data, error } = await supabase
      .from("feature_flags")
      .select("*, tenants(id,name,slug)")
      .order("tenant_id", { ascending: true, nullsFirst: true })
      .order("key", { ascending: true });

    if (error) {
      throw error;
    }

    return (data ?? []) as FeatureFlag[];
  },

  async setFlag(key: string, enabled: boolean, tenantId?: string | null) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_set_feature_flag", {
      p_key: key,
      p_enabled: enabled,
      p_tenant_id: tenantId ?? null,
    });

    if (error) {
      throw error;
    }
  },

  async listPackageDefaults(): Promise<FeatureFlagPackageDefault[]> {
    assertSupabaseConfigured();

    const { data, error } = await supabase
      .from("feature_flag_package_defaults")
      .select("*")
      .order("plan", { ascending: true })
      .order("key", { ascending: true });

    if (error) throw error;
    return (data ?? []) as FeatureFlagPackageDefault[];
  },

  async listUserOverrides(): Promise<FeatureFlagUserOverride[]> {
    assertSupabaseConfigured();

    const { data, error } = await supabase
      .from("feature_flag_user_overrides")
      .select("*, profiles(id,email,full_name)")
      .order("created_at", { ascending: false })
      .limit(200);

    if (error) throw error;
    return (data ?? []) as FeatureFlagUserOverride[];
  },

  async setPackageDefault(plan: TenantPlan, key: string, enabled: boolean) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_set_plan_feature_flag", {
      p_plan: plan,
      p_key: key,
      p_enabled: enabled,
    });

    if (error) throw error;
  },

  async setUserOverride(userId: string, key: string, enabled: boolean) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_set_user_feature_flag", {
      p_user_id: userId,
      p_key: key,
      p_enabled: enabled,
    });

    if (error) throw error;
  },

  async clearUserOverride(userId: string, key: string) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_clear_user_feature_flag", {
      p_user_id: userId,
      p_key: key,
    });

    if (error) throw error;
  },
};
